import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Box, Button, Typography, TextField, InputAdornment,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Checkbox, IconButton, alpha, useTheme, CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon, Close as CloseIcon,
  ExpandMore as ExpandMoreIcon, ShoppingCart as CartIcon
} from '@mui/icons-material';
import { supabase } from '../../../../supabaseClient';
import { formatCurrency } from './utils';

const ProductSelector = ({ open, onClose, onItemsAdded, companyId, invoice, existingLineItems = [] }) => {
  const theme = useTheme();
  const [products, setProducts] = useState([]);
  const [priceListItems, setPriceListItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedProducts, setExpandedProducts] = useState(new Set());
  const [selectedVariants, setSelectedVariants] = useState(new Map());

  useEffect(() => {
    if (open && companyId) fetchProducts();
    if (!open) { setSearchTerm(''); setSelectedVariants(new Map()); setExpandedProducts(new Set()); }
  }, [open, companyId]);

  useEffect(() => {
    if (invoice?.price_list_id && companyId) fetchPriceListItems(invoice.price_list_id);
    else setPriceListItems([]);
  }, [invoice?.price_list_id, companyId]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('products')
        .select('id, name, sku, brand:brand_id(id, name), variants:product_variants(id, name, sku, price, unit_price, volume_weight, volume_unit, attributes, is_active, pack_quantity)')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      setProducts(data || []);
    } catch (err) { console.error('Error fetching products:', err); }
    finally { setLoading(false); }
  };

  const fetchPriceListItems = async (priceListId) => {
    try {
      const { data, error } = await supabase.from('price_list_items').select('*').eq('price_list_id', priceListId);
      if (error) throw error;
      setPriceListItems(data || []);
    } catch (err) { console.error('Error fetching price list items:', err); }
  };

  const getPriceForVariant = (productId, variantId) => {
    if (priceListItems.length > 0) {
      const plItem = priceListItems.find(p => p.product_id === productId && (variantId ? p.variant_id === variantId : !p.variant_id));
      if (plItem) return parseFloat(plItem.case_price || plItem.price || 0);
    }
    return 0;
  };

  const getVariantKey = (productId, variantId) => `${productId}-${variantId || 'base'}`;

  const toggleProductExpand = (productId) => {
    setExpandedProducts(prev => { const n = new Set(prev); if (n.has(productId)) n.delete(productId); else n.add(productId); return n; });
  };

  const toggleVariantSelect = (product, variant) => {
    const key = getVariantKey(product.id, variant?.id);
    setSelectedVariants(prev => {
      const next = new Map(prev);
      if (next.has(key)) { next.delete(key); return next; }
      const plPrice = getPriceForVariant(product.id, variant?.id);
      const basePrice = parseFloat(variant?.price || variant?.unit_price || 0);
      const price = plPrice > 0 ? plPrice : basePrice;
      next.set(key, { product, variant, quantity: 1, unit_type: 'Case', unit_price: price });
      return next;
    });
  };

  const updateField = (key, field, value) => {
    setSelectedVariants(prev => { const n = new Map(prev); const it = n.get(key); if (it) n.set(key, { ...it, [field]: value }); return n; });
  };

  const filteredProducts = products.filter(p => {
    if (!searchTerm.trim()) return true;
    const term = searchTerm.toLowerCase();
    return p.name?.toLowerCase().includes(term) || p.brand?.name?.toLowerCase().includes(term) || p.sku?.toLowerCase().includes(term);
  });

  const handleConfirm = () => {
    const newItems = [];
    selectedVariants.forEach((item) => {
      const { product, variant, quantity, unit_type, unit_price } = item;
      const variantLabel = variant
        ? (variant.name || [variant.volume_weight, variant.volume_unit].filter(Boolean).join(' ') || variant.sku || 'Variant')
        : '';
      newItems.push({
        id: `new-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        product_id: product.id, product_name: product.name,
        variant_id: variant?.id || null, variant_name: variantLabel,
        description: variantLabel ? `${product.name} - ${variantLabel}` : product.name,
        quantity, unit_type, unit_price,
        units_per_case: variant?.pack_quantity || null,
        line_total: quantity * unit_price, total_price: quantity * unit_price
      });
    });
    if (newItems.length > 0) onItemsAdded(newItems);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth disableScrollLock
      PaperProps={{ sx: { borderRadius: 3, border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` } }}>
      <DialogTitle sx={{
        fontWeight: 500, color: 'primary.main',
        borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: alpha(theme.palette.primary.main, 0.05),
        display: 'flex', alignItems: 'center', justifyContent: 'space-between'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}><CartIcon />Select Products for Invoice</Box>
        <IconButton onClick={onClose} size="small"><CloseIcon /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ p: 2.5, borderBottom: `1px solid ${alpha(theme.palette.grey[400], 0.4)}` }}>
          <TextField fullWidth placeholder="Search products or brands..." value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)} size="small"
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon color="action" /></InputAdornment> }} />
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
        ) : (
          <TableContainer sx={{ maxHeight: 460 }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ '& .MuiTableCell-root': { fontWeight: 500, color: 'primary.main', bgcolor: `rgb(245,245,245)` } }}>
                  <TableCell sx={{ width: 48 }} />
                  <TableCell>Product Name</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell sx={{ width: 180 }}>Variant / Price</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.length === 0 ? (
                  <TableRow><TableCell colSpan={4} sx={{ textAlign: 'center', py: 4 }}><Typography color="text.secondary">No products found.</Typography></TableCell></TableRow>
                ) : filteredProducts.map((product) => {
                  const activeVariants = (product.variants || [])
                    .filter(v => v.is_active !== false)
                    .sort((a, b) => (parseFloat(b.volume_weight) || 0) - (parseFloat(a.volume_weight) || 0));
                  const isExpanded = expandedProducts.has(product.id);
                  const hasVariants = activeVariants.length > 0;

                  return (
                    <React.Fragment key={product.id}>
                      <TableRow hover sx={{ cursor: hasVariants ? 'pointer' : 'default', bgcolor: 'white' }}
                        onClick={() => hasVariants && toggleProductExpand(product.id)}>
                        <TableCell align="center">
                          {hasVariants && (
                            <ExpandMoreIcon sx={{
                              fontSize: 18, color: isExpanded ? 'primary.main' : 'text.disabled',
                              transform: isExpanded ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s'
                            }} />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontWeight={500}>{product.name}</Typography>
                          {product.sku && <Typography variant="caption" color="text.secondary">{product.sku}</Typography>}
                        </TableCell>
                        <TableCell><Typography variant="body2" color="text.secondary">{product.brand?.name || '—'}</Typography></TableCell>
                        <TableCell><Typography variant="caption" color="text.secondary">{hasVariants ? `${activeVariants.length} variant${activeVariants.length !== 1 ? 's' : ''}` : 'No variants'}</Typography></TableCell>
                      </TableRow>

                      {hasVariants && isExpanded && activeVariants.map((variant) => {
                        const key = getVariantKey(product.id, variant.id);
                        const isSelected = selectedVariants.has(key);
                        const sel = selectedVariants.get(key);
                        const plPrice = getPriceForVariant(product.id, variant.id);
                        const basePrice = parseFloat(variant.price || variant.unit_price || 0);
                        const displayPrice = plPrice > 0 ? plPrice : basePrice;
                        const variantLabel = variant.name || [variant.volume_weight, variant.volume_unit].filter(Boolean).join(' ') || variant.sku || 'Variant';

                        return (
                          <TableRow key={variant.id} sx={{
                            bgcolor: isSelected ? alpha(theme.palette.primary.main, 0.06) : alpha(theme.palette.grey[50], 1),
                            '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) }
                          }}>
                            <TableCell sx={{ pl: 3 }}>
                              <Checkbox size="small" checked={isSelected} onChange={() => toggleVariantSelect(product, variant)} />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.82rem', pl: 1 }}>{variantLabel}</Typography>
                              {variant.sku && <Typography variant="caption" color="text.disabled" sx={{ pl: 1 }}>{variant.sku}</Typography>}
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ color: displayPrice > 0 ? 'success.main' : 'text.disabled', fontWeight: 500, fontSize: '0.82rem' }}>
                                {displayPrice > 0 ? formatCurrency(displayPrice) : '—'}
                              </Typography>
                              {plPrice > 0 && <Typography variant="caption" color="text.disabled" sx={{ display: 'block' }}>Price List</Typography>}
                            </TableCell>
                            <TableCell>
                              {isSelected && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <TextField type="number" value={sel.quantity}
                                    onChange={(e) => updateField(key, 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                                    size="small" inputProps={{ min: 1 }} sx={{ width: 90 }}
                                    onClick={(e) => e.stopPropagation()} />
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                    {['Case', 'Unit'].map((ut) => (
                                      <Typography key={ut} variant="caption"
                                        onClick={(e) => { e.stopPropagation(); updateField(key, 'unit_type', ut); }}
                                        sx={{ cursor: 'pointer', fontWeight: sel.unit_type === ut ? 500 : 400, color: sel.unit_type === ut ? 'primary.main' : 'text.disabled', '&:hover': { color: 'primary.main' } }}>
                                        {ut}
                                      </Typography>
                                    ))}
                                  </Box>
                                </Box>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2.5, borderTop: `1px solid ${alpha(theme.palette.grey[400], 0.4)}`, justifyContent: 'space-between' }}>
        <Typography variant="body2" color="text.secondary">
          {selectedVariants.size > 0 ? `${selectedVariants.size} variant${selectedVariants.size !== 1 ? 's' : ''} selected` : 'No items selected'}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button onClick={onClose} sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={selectedVariants.size === 0}
            sx={{ borderRadius: 2, fontWeight: 500 }}>
            Add {selectedVariants.size > 0 ? `(${selectedVariants.size})` : ''}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default ProductSelector;
